import {Action, ActionReducer, createReducer, on} from '@ngrx/store';
import {getDeepCopyOfObject} from 'src/app/shared/shared-utils/object/object.utils';
import {
  IContactDetails,
  ILoginSteps,
  IPersonalDetails,
  IStateLogger,
  IUser,
  IUserLogger,
  LoginProgress,
} from '../models';
import * as UserActions from './user.actions';
import {updateUserProfileDetails} from './user.actions';
import {calculatePercentile} from '../../../shared/shared-utils/number/number.utils';
import {IGlobalError} from '../../../shared/shared-components/components/global-error-display/models/global-error';

export interface IUserState {
  afaPersistence: 'local' | 'session' | 'none';
  errors: IGlobalError[];
  isGlobalModalVisible: boolean;
  isManualLogin: boolean;
  isPageLoading: boolean;
  isUserDetailsLoading: boolean;
  isUserLoading: boolean;
  isUserLogOutModalOpen: boolean; // needs to change to modal type
  isUserLoggedIn: boolean;
  isUserProfileUpdating: boolean;
  isUserResetPasswordLoading: boolean;
  isUserResetPasswordSent: boolean;
  lastLogin: Date;
  lastSuccessfulProfileUpdate: Partial<IUser> | null;
  loginProgress: ILoginSteps | undefined;
  modalType: string[];
  user: IUser | null;
}

export const initialUserState: IUserState = {
  afaPersistence: 'session',
  errors: [],
  isGlobalModalVisible: false,
  isPageLoading: false,
  isUserLoading: false,
  isUserDetailsLoading: false,
  isUserLoggedIn: false,
  isUserLogOutModalOpen: false,
  isUserProfileUpdating: false,
  isUserResetPasswordLoading: false,
  isUserResetPasswordSent: false,
  lastLogin: undefined,
  lastSuccessfulProfileUpdate: null,
  loginProgress: undefined,
  isManualLogin: false,
  modalType: [],
  user: null,
};

const createUserReducer: ActionReducer<IUserState> = createReducer(
  getDeepCopyOfObject(initialUserState),
  //#region GLOBAL
  on(UserActions.setPropertyOnUserState, (state: IUserState, {property, value}) => {
    return {
      ...state,
      [property]: value,
    };
  }),
  on(UserActions.clearUserState, () => ({
    ...getDeepCopyOfObject(initialUserState),
  })),
  on(UserActions.setGlobalModalVisibility, (state: IUserState, {visibility, modalType}) => {
    return {
      ...state,
      isGlobalModalVisible: visibility,
      modalType: modalType,
    };
  }),
  on(UserActions.setAngularFireAuthPersistence, (state: IUserState, {persistence}) => ({
    ...state,
    afaPersistence: persistence,
  })),
  //#endregion
  //#region LOGIN
  on(UserActions.loginUser, (state: IUserState) => {
    return {
      ...state,
      isUserLoading: true,
      isUserLoggedIn: false,
      loginProgress: getDeepCopyOfObject(LoginProgress),
    };
  }),
  on(UserActions.loginUserSuccess, (state: IUserState, {user}) => ({
    ...state,
    isUserLoading: false,
    isUserLoggedIn: true,
    user,
  })),
  on(UserActions.setLoginProgress, (state: IUserState, {progress}) => {
    const stateProgress = state.loginProgress ? state.loginProgress : getDeepCopyOfObject(LoginProgress);
    const step = progress.currentStep || 1;
    return {
      ...state,
      loginProgress: {
        ...stateProgress,
        [step]: {
          ...stateProgress[step],
          completed: true,
        },
        progress: {
          ...stateProgress.progress,
          currentStep: step,
          percentComplete: calculatePercentile(step, stateProgress.progress.totalSteps),
        },
      },
    };
  }),
  on(UserActions.setManualLoginFlag, (state: IUserState, {isManualLogin}) => ({
    ...state,
    isManualLogin,
  })),
  //#endregion
  //#region LOG OUT USER
  on(UserActions.logOutUser, (state: IUserState) => ({
    ...state,
    isUserLoggedIn: false,
  })),
  on(UserActions.logOutUserSuccess, () => {
    window.location.reload();
    return {
      ...getDeepCopyOfObject(initialUserState),
    };
  }),
  //#endregion
  //#region GET USER
  on(UserActions.getUserDetailsByUserId, (state: IUserState) => ({
    ...state,
    isUserDetailsLoading: true,
  })),
  on(UserActions.getUserDetailsByUserIdSuccess, (state: IUserState, {user}) => ({
    ...state,
    isUserDetailsLoading: false,
    user,
  })),
  //#endregion
  //#region [User | User Profile] Update User Profile
  on(updateUserProfileDetails.updateUserProfile, (state) => ({
    ...state,
    isUserProfileUpdating: true,
    lastSuccessfulProfileUpdate: null,
  })),
  on(updateUserProfileDetails.updateUserProfileSuccess, (state, {data}) => {
    const patchContact: Partial<IContactDetails> = {
      ...(data?.contactDetails?.cellphone && {cellphone: data.contactDetails.cellphone}),
      ...(data?.contactDetails?.email && {email: data.contactDetails.email}),
    };
    const patchPersonal: Partial<IPersonalDetails> = {
      ...(data?.personalDetails?.firstname && {firstname: data.personalDetails.firstname}),
      ...(data?.personalDetails?.lastname && {lastname: data.personalDetails.lastname}),
    };
    const patchStateLogger: Partial<IStateLogger> = {
      ...(data?.logState && {...data.logState}),
    };
    const patchUserLogger: Partial<IUserLogger> = {
      ...(data?.logUser && {...data.logUser}),
    };
    const mergedContact: IContactDetails = {
      ...(state.user?.contactDetails ?? {} as IContactDetails),
      ...patchContact,
    };
    const mergedPersonal: IPersonalDetails = {
      ...(state.user?.personalDetails ?? {} as IPersonalDetails),
      ...patchPersonal,
    };
    const mergedStateLogger: IStateLogger = {
      ...(state.user?.logState ?? {} as IStateLogger),
      ...patchStateLogger,
    };
    const mergedUserLogger: IUserLogger = {
      ...(state.user?.logUser ?? {} as IUserLogger),
      ...patchUserLogger,
    };
    return {
      ...state,
      isUserProfileUpdating: false,
      lastSuccessfulProfileUpdate: data,
      user: {
        ...(state.user ?? {} as IUser),
        contactDetails: mergedContact,
        personalDetails: mergedPersonal,
        logState: mergedStateLogger,
        logUser: mergedUserLogger,
        pp: data?.pp ?? state.user?.pp,
        userName: data?.userName ?? state.user?.userName,
      } as IUser,
    };
  }),
  on(updateUserProfileDetails.updateUserProfileFailure, (state, {error}) => ({
    ...state,
    isUserProfileUpdating: false,
    errors: [...state.errors, error],
  })),
  //#endregion
  //#region RESET PASSWORD
  on(UserActions.resetUserPassword, (state: IUserState) => ({
    ...state,
    isUserResetPasswordLoading: true,
    isUserResetPasswordSent: false,
  })),
  on(UserActions.resetUserPasswordSuccess, (state: IUserState) => ({
    ...state,
    isUserResetPasswordLoading: false,
    isUserResetPasswordSent: true,
  })),
  on(UserActions.resetUserPasswordFailure, (state: IUserState) => ({
    ...state,
    isUserResetPasswordLoading: false,
    isUserResetPasswordSent: false,
  })),
  //#endregion
);
export const userReducer = (state: IUserState, action: Action): IUserState =>
  createUserReducer(state, action);
